/**
 * Adaptive difficulty selector (PLAN-Z M10, PLAN.md §4): a pure function from
 * the attempts history to the next {subtest, difficulty}. The exam block
 * mode deliberately does NOT use this — it mirrors the real test's fixed
 * composition.
 *
 * Cold start: 20/40/40 across low/medium/high, uniform across subtests.
 * Then, over a rolling window of the last 20 attempts per
 * (subtest, difficulty) bucket:
 *
 *   deficit = max(0, 0.8 − accuracy) + max(0, median/75s − 1)
 *   weight  = coldBase × (1 + k·deficit), clamped to
 *             [coldBase × 0.25, coldBase × 3]
 *
 * so a struggling bucket gains weight without any bucket disappearing (you
 * still meet high items while weak and still see low items once strong).
 * Buckets with fewer than 5 attempts keep the cold-start weight so a single
 * unlucky miss cannot distort the mix. Every weight stays > 0.
 *
 * DOM-free and dependency-free (PLAN-Z convention 4).
 */
import type { Attempt } from '../data/local.ts'
import { bucketKey, type BucketKey } from '../stats/compute.ts'
import type { Difficulty, Subtest } from '../generators/types.ts'

export const TARGET_ACCURACY = 0.8
export const BENCHMARK_MS = 75_000
export const WINDOW_SIZE = 20
export const MIN_ATTEMPTS = 5
export const K = 2
export const WEIGHT_FLOOR = 0.25
export const WEIGHT_CEIL = 3

const DIFFICULTIES: Difficulty[] = ['low', 'medium', 'high']
const COLD_SHARE: Record<Difficulty, number> = { low: 0.2, medium: 0.4, high: 0.4 }

/** 20/40/40 across difficulties, uniform across subtests. */
export function coldStartWeights(subtests: Subtest[]): Map<BucketKey, number> {
  const share = 1 / Math.max(1, subtests.length)
  const weights = new Map<BucketKey, number>()
  for (const subtest of subtests) {
    for (const difficulty of DIFFICULTIES) {
      weights.set(bucketKey(subtest, difficulty), share * COLD_SHARE[difficulty])
    }
  }
  return weights
}

/** Accuracy shortfall + pace shortfall (PLAN.md §4). */
export function deficitOf(accuracy: number, medianMs: number): number {
  const accuracyShortfall = Math.max(0, TARGET_ACCURACY - accuracy)
  const paceShortfall = Math.max(0, medianMs / BENCHMARK_MS - 1)
  return accuracyShortfall + paceShortfall
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function median(sorted: number[]): number {
  if (sorted.length === 0) return 0
  const mid = (sorted.length - 1) / 2
  const lo = Math.floor(mid)
  const hi = Math.ceil(mid)
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (mid - lo)
}

/** Weight per (subtest, difficulty) bucket. Buckets with fewer than
 * MIN_ATTEMPTS attempts in their window keep the cold-start weight. */
export function computeWeights(attempts: Attempt[], subtests: Subtest[]): Map<BucketKey, number> {
  const cold = coldStartWeights(subtests)
  const groups = new Map<BucketKey, Attempt[]>()
  for (const attempt of attempts) {
    if (!subtests.includes(attempt.subtest)) continue
    const key = bucketKey(attempt.subtest, attempt.difficulty)
    const group = groups.get(key) ?? []
    group.push(attempt)
    groups.set(key, group)
  }

  const weights = new Map<BucketKey, number>()
  for (const [key, base] of cold) {
    const windowed = [...(groups.get(key) ?? [])]
      .sort((a, b) => a.answeredAt.localeCompare(b.answeredAt))
      .slice(-WINDOW_SIZE)
    if (windowed.length < MIN_ATTEMPTS) {
      weights.set(key, base)
      continue
    }
    const accuracy = windowed.filter((a) => a.correct).length / windowed.length
    const durations = windowed.map((a) => a.durationMs).sort((x, y) => x - y)
    const deficit = deficitOf(accuracy, median(durations))
    weights.set(key, clamp(base * (1 + K * deficit), base * WEIGHT_FLOOR, base * WEIGHT_CEIL))
  }
  return weights
}

/** Weighted random pick of the next (subtest, difficulty). */
export function pickNext(
  attempts: Attempt[],
  subtests: Subtest[],
  rng: () => number = Math.random,
): { subtest: Subtest; difficulty: Difficulty } {
  const weights = computeWeights(attempts, subtests)
  const entries = [...weights.entries()]
  const total = entries.reduce((sum, [, w]) => sum + w, 0)
  let draw = rng() * total
  for (const [key, weight] of entries) {
    draw -= weight
    if (draw <= 0) {
      const [subtest, difficulty] = key.split('/') as [Subtest, Difficulty]
      return { subtest, difficulty }
    }
  }
  const [subtest, difficulty] = entries[entries.length - 1][0].split('/') as [Subtest, Difficulty]
  return { subtest, difficulty }
}
